// ============================================================================
// CabinetArbitrationTab.tsx — вкладка «Арбитраж» кабинета.
//
// Материалы для разбора — СУЩЕСТВУЮЩИЕ документы базы. Новых proto и
// серверных методов нет: состав приходит с сервера (MetadataService.ListDocuments),
// права — из GetDocument.rights (SSoT сервера), данные — AutoListForm через
// ListTableData (как в «Предприятии»).
//
// В базе маркетплейса (hydra.market, §3.1) роль «Арбитр» получает ТОЛЬКО
// чтение заявок и откликов — вкладка показывает именно эти документы. Если
// в базе есть собственные документы арбитража (код/название содержат
// «арбитраж», «спор», «претензия», «жалоба»), они также попадают в список.
// Если подходящих документов нет или роль не имеет чтения — понятное пустое
// состояние, без пустых таблиц и «вечной загрузки».
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Scale } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { createLogger } from '@/lib/logger';
import { AutoListForm } from '@/components/autoforms/AutoListForm';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { Button } from '@/components/ui/button';
import { TabBar } from '@/components/ui/TabBar';
import type { AutoListFormProps } from '@/components/autoforms/autoListHelpers';

const log = createLogger('CabinetArbitrationTab');

/** Документы поставки «Маркетплейс услуг» — очередь арбитра (§3.1). */
const KNOWN_DOCUMENTS: Record<string, { title: string; order: number }> = {
  mkt_requests: { title: 'Заявки', order: 10 },
  mkt_responses: { title: 'Отклики', order: 20 },
};

/** Код документа говорит об арбитраже (латиницей). */
const ARBITRATION_CODE = /(^|[_\-. ])(arbitr|dispute)([_\-. ]|$)/i;
/** Название/описание говорит об арбитраже (по-русски). */
const ARBITRATION_TEXT = /арбитраж|спор|претенз|жалоб/i;

interface ArbitrationDocument {
  code: string;
  title: string;
  order: number;
  rights: AutoListFormProps['rights'];
}

function isArbitrationDocument(doc: any): boolean {
  const code = String(doc?.code || '');
  if (KNOWN_DOCUMENTS[code]) return true;
  if (ARBITRATION_CODE.test(code)) return true;
  const text = `${doc?.name || ''} ${doc?.description || ''}`;
  return ARBITRATION_TEXT.test(text);
}

function titleOf(doc: any): string {
  const code = String(doc?.code || '');
  const known = KNOWN_DOCUMENTS[code];
  if (known) return known.title;
  return String(doc?.description || doc?.name || code);
}

export function CabinetArbitrationTab() {
  const [docs, setDocs] = useState<ArbitrationDocument[]>([]);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [record, setRecord] = useState<{ id?: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sdk = getSDK();
      const list: any = await sdk.metadata.listDocuments({});
      const candidates = ((list?.items || []) as any[]).filter(isArbitrationDocument);

      // Права — ТОЛЬКО с сервера: документ без чтения не показываем (иначе
      // вкладка вела бы в список, который всё равно ответит отказом).
      const checked = await Promise.all(candidates.map(async (doc): Promise<ArbitrationDocument | null> => {
        const docCode = String(doc?.code || '');
        if (!docCode) return null;
        try {
          const full: any = await sdk.metadata.getDocument({ id: docCode });
          const rights = full?.rights || null;
          if (rights?.canRead === false) return null;
          return {
            code: docCode,
            title: titleOf(doc),
            order: KNOWN_DOCUMENTS[docCode]?.order ?? 100,
            rights,
          };
        } catch (e) {
          // Нет чтения/документ скрыт функциональной опцией — не ошибка вкладки.
          log.warn(`Документ «${docCode}» недоступен, пропущен`, { error: String(e) });
          return null;
        }
      }));

      const items = checked.filter((d): d is ArbitrationDocument => d !== null);
      items.sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.title.localeCompare(b.title, 'ru');
      });
      setDocs(items);
      setCode((current) => (items.some((d) => d.code === current) ? current : (items[0]?.code || '')));
    } catch (e) {
      log.error('Не удалось загрузить материалы арбитража', { error: String(e) });
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const active = useMemo(
    () => docs.find((d) => d.code === code) || docs[0] || null,
    [docs, code],
  );

  const selectType = (next: string) => {
    setCode(next);
    setRecord(null);
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <div className="max-w-md break-words text-sm text-destructive">{error}</div>
        <Button size="sm" variant="outline" onClick={() => void load()}>
          <RefreshCw className="mr-1 h-3 w-3" />
          Повторить
        </Button>
      </div>
    );
  }

  if (!active) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <Scale className="h-6 w-6 text-muted-foreground" />
        <div className="max-w-md text-sm text-muted-foreground">
          Нет доступных материалов арбитража. В базе маркетплейса это заявки и отклики
          (роль «Арбитр»); доступ выдаёт администратор базы.
        </div>
        <Button size="sm" variant="outline" onClick={() => void load()}>
          <RefreshCw className="mr-1 h-3 w-3" />
          Обновить
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b bg-background/60 px-3 py-1.5">
        <div className="mb-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Scale className="h-3.5 w-3.5" />
          <span>Материалы для разбора · права и данные проверяет сервер</span>
        </div>
        {docs.length > 1 && (
          <TabBar
            tabs={docs.map((d) => ({ id: d.code, label: d.title }))}
            active={active.code}
            onChange={selectType}
            group="cabinet-arbitration-documents"
          />
        )}
      </div>

      <div className="min-h-0 flex-1">
        <AutoListForm
          key={`document:${active.code}`}
          objectType="document"
          objectName={active.code}
          rights={active.rights}
          onRowDoubleClick={(row) => setRecord({ id: String(row.id || '') })}
          onCreate={active.rights?.canCreate ? () => setRecord({}) : undefined}
          foldersPanel
        />
      </div>

      {record && (
        <AutoObjectForm
          objectType="document"
          objectName={active.code}
          objectId={record.id}
          onClose={() => setRecord(null)}
        />
      )}
    </div>
  );
}
